(function () {
  'use strict';

  var SELECTOR = '[data-secondary-product-image]';
  var CARD_SELECTOR = '.product-card-wrapper';
  var QUIET_WINDOW = 1200;
  var DELAY_AFTER_LOAD = 1500;
  var stopped = false;
  var warmupReady = false;
  var warmupScheduled = false;
  var warmQueue = [];
  var warmEnqueued = typeof WeakSet === 'function' ? new WeakSet() : null;
  var warmActive = false;
  var queueGeneration = 0;
  var quietTimer = null;
  var lastActivity = 0;

  function setPriority(image, priority) {
    image.fetchPriority = priority;
    image.setAttribute('fetchpriority', priority);
  }

  function hydrate(image, priority) {
    if (!image || !image.isConnected) return Promise.resolve(false);
    if (image.dataset.secondaryLoaded === 'true') return Promise.resolve(true);
    if (image.dataset.secondaryLoading === 'true') {
      if (priority !== 'low') setPriority(image, 'high');
      return image.secondaryLoadPromise;
    }

    setPriority(image, priority || 'high');
    image.loading = 'eager';
    image.setAttribute('loading', 'eager');
    image.dataset.secondaryLoading = 'true';
    image.secondaryLoadPromise = new Promise(function (resolve) {
      var finished = false;
      var finish = function (ready) {
        if (finished) return;
        finished = true;
        image.removeEventListener('load', loaded);
        image.removeEventListener('error', failed);
        image.dataset.secondaryLoading = 'false';
        image.dataset.secondaryLoaded = String(ready);
        image.parentElement?.classList.toggle('media--secondary-ready', ready);
        resolve(ready);
      };
      var failed = function () { finish(false); };
      var loaded = function () {
        if (!image.naturalWidth) return failed();
        if (typeof image.decode === 'function') image.decode().then(function () { finish(true); }, failed);
        else finish(true);
      };
      image.addEventListener('load', loaded);
      image.addEventListener('error', failed);
      if (image.dataset.secondarySrcset) image.srcset = image.dataset.secondarySrcset;
      if (image.dataset.secondarySrc) image.src = image.dataset.secondarySrc;
      // A cached result can already be complete. Ignore the transparent placeholder.
      if (image.complete && image.currentSrc && image.currentSrc.indexOf('data:') !== 0) loaded();
    });
    return image.secondaryLoadPromise;
  }

  function canWarm() {
    if (!window.matchMedia || !window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
      return false;
    }

    var connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    if (!connection) return true;
    if (connection.saveData) return false;
    return !/^(slow-2g|2g|3g)$/.test(connection.effectiveType || '');
  }

  function isQuiet() {
    return !lastActivity || Date.now() - lastActivity >= QUIET_WINDOW;
  }

  function enqueue(image) {
    if (!image || !image.isConnected || image.dataset.secondaryLoaded === 'true') return;
    if (warmEnqueued) {
      if (warmEnqueued.has(image)) return;
      warmEnqueued.add(image);
    } else if (warmQueue.indexOf(image) !== -1) {
      return;
    }
    warmQueue.push(image);
  }

  function processQueue() {
    if (stopped || warmActive || document.hidden || !isQuiet() || !canWarm()) return;

    var image;
    while (warmQueue.length) {
      image = warmQueue.shift();
      if (image && image.isConnected && image.dataset.secondaryLoaded !== 'true') break;
      image = null;
    }
    if (!image) return;

    warmActive = true;
    var generation = queueGeneration;
    var advance = function () {
      if (generation !== queueGeneration) return;
      warmActive = false;
      processQueue();
    };
    hydrate(image, 'low').then(advance);
  }

  function collectAndStart() {
    if (stopped) return;
    if (document.hidden) return;
    if (!canWarm()) return;

    warmupReady = true;
    document.querySelectorAll(SELECTOR).forEach(enqueue);
    processQueue();
  }

  function scheduleWarmup() {
    if (warmupScheduled || stopped) return;
    warmupScheduled = true;

    window.setTimeout(function () {
      if (stopped) return;
      var start = function () {
        warmupReady = true;
        collectAndStart();
      };

      if (typeof window.requestIdleCallback === 'function') {
        window.requestIdleCallback(start, { timeout: 3000 });
      } else {
        window.setTimeout(start, 500);
      }
    }, DELAY_AFTER_LOAD);
  }

  function markActivity() {
    lastActivity = Date.now();
    if (quietTimer) window.clearTimeout(quietTimer);
    quietTimer = window.setTimeout(function () {
      quietTimer = null;
      if (warmupReady) processQueue();
    }, QUIET_WINDOW);
  }

  function hydrateCard(event) {
    var target = event.target;
    if (!target || !target.closest) return;
    var card = target.closest(CARD_SELECTOR);
    if (!card) return;
    hydrate(card.querySelector(SELECTOR));
  }

  function onPointerOver(event) {
    if (event.pointerType === 'touch') return;
    hydrateCard(event);
  }

  function onVisibilityChange() {
    if (!document.hidden && warmupReady) collectAndStart();
  }

  function stop() {
    stopped = true;
    queueGeneration += 1;
    warmQueue.length = 0;
    warmActive = false;
    if (quietTimer) window.clearTimeout(quietTimer);
    quietTimer = null;
  }

  function restartAfterBfcache(event) {
    if (!event.persisted) return;
    stopped = false;
    warmupReady = false;
    warmupScheduled = false;
    warmQueue.length = 0;
    warmActive = false;
    warmEnqueued = typeof WeakSet === 'function' ? new WeakSet() : null;
    lastActivity = 0;
    quietTimer = null;
    scheduleWarmup();
  }

  document.addEventListener('pointerover', onPointerOver);
  document.addEventListener('focusin', hydrateCard);
  document.addEventListener('pointerdown', markActivity, true);
  document.addEventListener('touchstart', markActivity, true);
  document.addEventListener('wheel', markActivity, true);
  document.addEventListener('keydown', markActivity, true);
  document.addEventListener('visibilitychange', onVisibilityChange);
  window.addEventListener('pagehide', stop);
  window.addEventListener('pageshow', restartAfterBfcache);

  if (typeof MutationObserver === 'function') {
    new MutationObserver(function (mutations) {
      if (stopped || !warmupReady || document.hidden || !canWarm()) return;
      mutations.forEach(function (mutation) {
        mutation.addedNodes.forEach(function (node) {
          if (node.nodeType !== 1) return;
          if (node.matches(SELECTOR)) enqueue(node);
          node.querySelectorAll(SELECTOR).forEach(enqueue);
        });
      });
      processQueue();
    }).observe(document.documentElement, { childList: true, subtree: true });
  }

  if (document.readyState === 'complete') {
    scheduleWarmup();
  } else {
    window.addEventListener('load', scheduleWarmup, { once: true });
  }
})();
